// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { AsyncPipe, NgIf } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  Input,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { LastDataSourceRefreshModel } from 'src/app/_interfaces/pattern-match/last-data-source-refresh-model';
import { SystemDetailsModel } from 'src/app/api/remote-api/.portal.generated/models';
import { CoilService } from 'src/app/modules/coils/services/coil.service';
import { LastdataSourceRefreshInformationsService } from 'src/app/modules/portal/services/lastdataSourceRefreshInformations.service';
import { APICallGateComponent } from 'src/app/modules/shared/components/api-call-gate.component';
import {
  isMRSystem,
  isXPSystem,
} from 'src/app/modules/shared/utils/modalityHelpers';
import { TubeService } from 'src/app/modules/tube/services/tube.service';
import { PortalWebAppConfigurationService } from '../../services/portal-web-app-configuration.service';
import { SystemOverviewService } from '../../services/systemOverview.service';
import { LastDataSourceRefreshStatusComponent } from './lastDataSourceRefreshStatus/lastDataSourceRefreshStatus.component';
import { LastDataSourceRefreshTubeCoilComponent } from './lastDataSourceRefreshTubeCoil/lastDataSourceRefreshTubeCoil.component';
import {
  RunTaskButtonComponent,
  TaskAPI,
} from './run-task-button/run-task-button.component';
import { SRSRefreshButtonComponent } from './srsRefreshButton/srsRefreshButton.component';

@Component({
  selector: 'app-lastDataSourceRefresh',
  templateUrl: './lastDataSourceRefresh.component.html',
  styleUrls: ['./lastDataSourceRefresh.component.scss'],
  standalone: true,

  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    APICallGateComponent,
    NgIf,
    LastDataSourceRefreshTubeCoilComponent,
    LastDataSourceRefreshStatusComponent,
    RunTaskButtonComponent,
    SRSRefreshButtonComponent,
    AsyncPipe,
    TranslateModule,
  ],
})
export class LastDataSourceRefreshComponent implements OnChanges, OnDestroy {
  destroy$ = new Subject<void>();

  @Input({ required: true }) systemDetails!: SystemDetailsModel;
  @Input({ required: true }) modalityName!: string;

  // main data source request service
  lastDataSourceRefreshInformations =
    this.lastDataSourceRefreshInformationsService
      .lastDataSourceRefreshInformations;

  get isXPSystem(): boolean {
    return isXPSystem(this.systemDetails.modalityId);
  }

  get isMRSystem(): boolean {
    return isMRSystem(this.systemDetails.modalityId);
  }

  // last data source info. massaged for ui already
  data$ = (source: string): Observable<LastDataSourceRefreshModel> =>
    this.lastDataSourceRefreshInformations.lastSuccessfulData$.pipe(
      map((dataSourceModel) => {
        const lastDataSourceRefreshState: LastDataSourceRefreshModel[] =
          dataSourceModel
            .filter(({ dataSource }) => dataSource === source)
            .map((m) => ({
              statusHeader: m?.dataSource,
              updatedSince: m?.lastUpdateDateTime,
              updatedPatternSince: m?.lastPatternDateTime,
              linkToXmart: m.linkToDocument ? m.linkToDocument.toString() : '',
            }));

        //This is only for MR systems to link to X-MART with new link
        //The old link is directly provided by backend in linkToDocument from an ETL job
        //Temporary solution until backend provides the new link directly or we remove X-MART linking completely in backend
        if (this.isMRSystem && lastDataSourceRefreshState[0].linkToXmart) {
          const X_MART_URL = `https://xmartticker.siemens-healthineers.com/CSMR_XMART_ErrorPattern/asp/index.aspx?SNR=${this.systemDetails.systemSerialNumber}`;
          lastDataSourceRefreshState[0].linkToXmart = X_MART_URL;
        }
        return lastDataSourceRefreshState[0];
      }),
    );

  constructor(
    private lastDataSourceRefreshInformationsService: LastdataSourceRefreshInformationsService,
    protected systemOverviewService: SystemOverviewService,
    protected tubeService: TubeService,
    protected coilService: CoilService,
    protected configService: PortalWebAppConfigurationService,
  ) {}

  get taskAPI(): TaskAPI {
    return {
      trigger: this.systemOverviewService.triggerSrsDataCollection,
      getStatus: this.systemOverviewService.getSrsDataCollectionStatus,
    };
  }

  ngOnChanges(): void {
    this.lastDataSourceRefreshInformations.request({
      modality: this.modalityName,
      systemMaterialNumber: this.systemDetails.systemMaterialNumber,
      systemSerialNumber: this.systemDetails.systemSerialNumber,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
  }
}
